<template>
  <div class="partially-denied--container">
    <JadeBox hasPadding>
      <div class="box1--container">
        <div class="box1__text--container">
          <jade-text
            weight="semibold"
            variant="heading-medium"
          >
            {{ $t('box1.title') }}
          </jade-text>
          <jade-text variant="text-medium">
            {{ $t('box1.subtitle') }}
          </jade-text>
        </div>
        <div class="box1__img--container">
          <img :src="require(`@/assets/kyc/step_qr_code_1.svg`).default" />
        </div>
      </div>
    </JadeBox>

    <JadeBox>
      <div class="py-5 pr-5">
        <div class="pl-5">
          <jade-text
            weight="semibold"
            variant="heading-xsmall"
            class="qrcode--hide"
          >
            {{ $t('box2.title') }}
          </jade-text>
        </div>

        <div class="box2--container">
          <div class="box2-inner">
            <div class="box2__list--container">
              <div class="pl-5">
                <jade-text
                  variant="overline"
                  :color="COLOR_ENUM.MEDIUM"
                >
                  {{ $t('box2.subtitle') }}
                </jade-text>
              </div>
              <jade-list-item :label="$t('box2.list-item-1')">
                <template #leading>
                  <jade-icon-shape
                    use="camera"
                    :size="SIZE_ENUM.SMALL"
                  />
                </template>
              </jade-list-item>

              <jade-list-item :label="$t('box2.list-item-2')">
                <template #leading>
                  <jade-icon-shape
                    use="person-check"
                    :size="SIZE_ENUM.SMALL"
                    variant="brand"
                  />
                </template>
              </jade-list-item>

              <jade-list-item :label="$t('box2.list-item-3')">
                <template #leading>
                  <jade-icon-shape
                    use="file-folded"
                    :size="SIZE_ENUM.SMALL"
                    variant="brand"
                  />
                </template>
              </jade-list-item>
            </div>
            <qr-code-kyc @qrCodeExpired="qrCodeExpired" />
          </div>
          <div class="divider">
            <span>OU</span>
          </div>
          <div class="outside--container">
            <jade-button
              v-if="checkKycStatusEnable"
              :loading="loadingKyc"
              :size="SIZE_ENUM.MEDIUM"
              variant="primary-solid"
              class="outside__button--container"
              @click="$emit('updateKycStatus')"
            >
              {{ $t('outside.button') }}
            </jade-button>

            <jade-button
              class="outside__button--container"
              :size="SIZE_ENUM.MEDIUM"
              variant="neutral-subtle"
              @click="redirectToKycExternal"
            >
              {{ $t('outside.text') }}
            </jade-button>
          </div>
          <div class="outside__mobile--container">
            <jade-button
              class="outside__button--container"
              :size="SIZE_ENUM.MEDIUM"
              variant="primary-solid"
              @click="redirectToKycExternal"
            >
              {{ $t('outside.button-mobile') }}
            </jade-button>
          </div>
        </div>
      </div>
    </JadeBox>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import JadeBox from '@jade/JadeBox/index.vue'
import JadeText from '@jade/JadeText/index.vue'
import JadeListItem from '@jade/JadeListItem/index.vue'
import JadeButton from '@jade/JadeButton/index.vue'
import JadeIconShape from '@jade/JadeIconShape/index.vue'

import QrCodeKyc from './components/QrCodeKyc.vue'

import COLOR_ENUM from '@jade/enums/colors.enum'
import SIZE_ENUM from '@jade/enums/sizes.enum'

const ONE_SECOND_IN_MILLISECONDS = 1000
const ONE_MINUTE_IN_SECONDS = 60

const RETRY_STATUS_LIMIT = 10
const RETRY_STATUS_INTERVAL_IN_MILLISECONDS =
  ONE_MINUTE_IN_SECONDS * ONE_SECOND_IN_MILLISECONDS

export default {
  name: 'PartiallyDenied',
  components: {
    JadeBox,
    JadeText,
    JadeListItem,
    JadeButton,
    JadeIconShape,
    QrCodeKyc
  },
  data () {
    return {
      getStatusIntervalRef: null,
      getStatusIntervalCount: 0,
      COLOR_ENUM,
      SIZE_ENUM,
      checkKycStatusEnable: false
    }
  },
  computed: {
    ...mapGetters(['kycQrCode', 'loadingKycQrCode', 'loadingKyc'])
  },
  methods: {
    getKycStatusInterval () {
      this.getStatusIntervalRef = setInterval(() => {
        this.getStatusIntervalCount++
        if (this.getStatusIntervalCount >= RETRY_STATUS_LIMIT) {
          this.checkKycStatusEnable = true
          clearInterval(this.getStatusIntervalRef)
        }
        this.$emit('updateKycStatus')
      }, RETRY_STATUS_INTERVAL_IN_MILLISECONDS)
    },

    redirectToKycExternal () {
      window.open(this.kycQrCode.url, '_blank')
    },
    qrCodeExpired () {
      this.checkKycStatusEnable = true
    }
  },
  created () {
    this.getKycStatusInterval()
  }
}
</script>

<style scoped>
@import '@stone-payments/jade-design-tokens/pagarme/space.css';

.partially-denied--container,
.box1__text--container,
.box2__list--container {
  display: flex;
  flex-direction: column;
}

.box2__list--container {
  align-items: center;
}

.box1--container,
.box1__img--container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.box2--container {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: var(--color-slate-alpha2);
  font-size: var(--theme-text-small-font-size, 12px);
  font-weight: 600;
  margin-top: var(--global-space-150, 12px);
  margin-bottom: var(--global-space-100, 8px);
  padding: var(--global-space-100, 8px) 0 var(--global-space-100, 8px)
    var(--global-space-250, 20px);
  width: 100%;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid var(--color-slate-alpha7);
}

.divider::before {
  margin-right: var(--global-space-150, 12px);
}

.divider::after {
  margin-left: var(--global-space-150, 12px);
}

.partially-denied--container {
  max-width: 540px;
  gap: var(--global-space-200, 16px);
  padding: 0 var(--global-space-250, 20px);
}

.box1--container {
  flex-direction: column-reverse;
  text-align: center;
  gap: var(--global-space-200, 16px);
}
.box1__text--container {
  gap: var(--global-space-100, 8px);
}

.box1__img--container {
  min-width: 72px;
}

.box2__list--container {
  gap: var(--global-space-100, 12px);
}

.qrcode--hide {
  display: none;
}

.qrcode--container {
  position: relative;
}

.qrcode__img {
  width: 200px;
  height: 200px;
  background-size: contain;
}

.qrcode--opacity {
  opacity: 0.1;
}

.qrcode__button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
}

.outside--container {
  display: none;
}
.outside__button--container {
  width: 100%;
}
.outside__mobile--container {
  display: flex;
  margin-top: var(--global-space-300, 24px);
  padding-left: var(--global-space-250, 20px);
  width: 100%;
}

@media (min-width: 768px) {
  .partially-denied--container {
    width: 668px;
    max-width: 668px;
  }

  .box2__list--container {
    width: 100%;
    align-items: flex-start;
  }

  .box1--container {
    text-align: inherit;
    flex-direction: row;
  }
  .box2--container {
    padding-top: var(--global-space-400, 32px);
    justify-content: space-between;
  }
  .box2-inner {
    display: flex;
  }

  .qrcode--hide {
    display: flex;
  }
  .outside--container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--global-space-200, 16px);
    width: 100%;
  }
  .outside--container > button {
    max-width: 305px;
  }

  .outside__mobile--container {
    display: none;
  }
}

@media (max-width: 767px) {
  .divider {
    display: none;
  }
}
</style>

<i18n>
    {
      "pt-BR": {
        "box1": {
          "title": "Complete seu cadastro",
          "subtitle": "Envie os documentos necessários para confirmar sua identidade e ativar sua conta."
        },
        "box2": {
          "title": "Escaneie o QR Code usando a câmera do seu celular",
          "subtitle": "confira nossas dicas",
          "list-item-1": "Escolha um lugar iluminado para tirar as fotos",
          "list-item-2": "Tenha cuidado para as fotos não saírem tremidas",
          "list-item-3": "Retire a capinha do documento para evitar reflexos",
          "time-text": "Expira em",
          "new-qr-code": "Criar novo QR Code"
        },
        "outside": {
          "text": "Envie os documentos por esse dispositivo",
          "button": "Já enviei as fotos",
          "button-mobile": "Enviar documentos"
        }
      },
      "en-US": {
        "box1": {
          "title": "Finish your registration",
          "subtitle": "Submit the required documents to verify your identity and activate your account."
        },
        "box2": {
          "title": "Scan the QR Code using your phone's camera",
          "subtitle": "check out our tips",
          "list-item-1": "Choose a bright place to take the photos",
          "list-item-2": "Be careful so the photos don't come out blurry",
          "list-item-3": "Remove the document cover to avoid reflections",
          "time-text": "Expires in",
          "new-qr-code": "Create new QR Code"
        },
        "outside": {
          "text": "Send the documents through this device",
          "button": "I already sent the photos",
          "button-mobile": "Send documents"
        }
      }
    }
</i18n>
